/**
 * The checkout contract, in one place, mirrored from the backend.
 *
 * SOURCE OF TRUTH: mdm_comics_backend/app/schemas/checkout.py
 *     product_id: int = Field(..., gt=0)
 *     quantity:   int = Field(..., gt=0, le=100)
 *     items:      List[CheckoutItem] = Field(..., min_length=1, max_length=50)
 *
 * A backend test reads THIS FILE and asserts the numbers match the schema, so a change
 * on either side that is not made on the other fails CI rather than shipping.
 *
 * ⛔ WHY resolveProductId EXISTS AT ALL. The cart guard and CheckoutForm each decided
 * independently which id an item has, and they used DIFFERENT OPERATORS:
 *
 *     guard         Number(item.product_id ?? item.id)
 *     CheckoutForm  item.product_id || item.id
 *
 * `??` falls through only for null/undefined; `||` falls through for ANY falsy value.
 * They diverge on exactly the inputs a guard exists to handle:
 *
 *     {product_id: 0,  id: 7}   guard -> 0 -> REJECTED   checkout -> 7 -> payable
 *     {product_id: '', id: 7}   guard -> NaN -> REJECTED checkout -> 7 -> payable
 *
 * So the guard DELETED AN ITEM CHECKOUT WOULD HAVE PAID FOR, silently, from the buyer's
 * own cart. It errs safe on money, but the justification for filtering at all is that a
 * dropped item is recoverable by re-adding it — and this item was never malformed, so
 * the buyer loses something they chose with no message and no way to know what.
 *
 * ⭐ Changing `??` to `||` would have fixed the symptom and left TWO implementations of
 * "which id does this item have", free to drift again. One function, used on both sides,
 * makes the drift impossible rather than merely corrected.
 */

/** schemas/checkout.py:22 — quantity: int = Field(..., gt=0, le=100) */
export const MAX_QUANTITY_PER_ITEM = 100;

/** schemas/checkout.py — items: List[CheckoutItem] = Field(..., min_length=1, max_length=50) */
export const MAX_CART_ITEMS = 50;

/**
 * Which id does this cart item have?
 *
 * ⚠ `||` IS DELIBERATE AND MUST NOT BE "MODERNISED" TO `??`. This mirrors what the
 * checkout payload actually sends, and a product_id of 0 or '' is not a usable id — it
 * must fall through to `id`, exactly as the payload builder does. Switching to `??`
 * re-introduces the divergence this function was extracted to remove.
 *
 * Returns the raw resolved value (or null); each caller coerces as it needs to — the
 * guard to a Number for validation, the payload builder as-is.
 */
export function resolveProductId(item) {
  if (item === null || typeof item !== 'object') return null;
  return item.product_id || item.id || null;
}

/**
 * ⛔ A BUNDLE ID AND A PRODUCT ID CAN BE THE SAME NUMBER.
 *
 * Cart lines are matched on `item.id`, and bundles and products have INDEPENDENT id sequences.
 * Bundle 7 and Product 7 are different things that collide on that key: add one, then the
 * other, and the second silently increments the first's quantity. The buyer gets two of a thing
 * they asked for once and none of the thing they asked for second -- with a total that looks
 * right.
 *
 * So a cart line is identified by KIND + ID, never by id alone.
 */
export const LINE_KIND_PRODUCT = 'product';
export const LINE_KIND_BUNDLE = 'bundle';

/** The kind of a cart line. Absent `kind` means product: every line written before bundles
 *  existed is a product, and a localStorage cart persists across deploys. */
export function lineKind(item) {
  return item?.kind === LINE_KIND_BUNDLE ? LINE_KIND_BUNDLE : LINE_KIND_PRODUCT;
}

/** The collision-proof identity of a cart line, e.g. "bundle:7" vs "product:7". */
export function lineKey(item) {
  return `${lineKind(item)}:${resolveProductId(item)}`;
}

/**
 * The checkout payload entry for a cart line.
 *
 * ⛔ THIS IS WHY resolveProductId ALONE IS NOT ENOUGH ANY MORE. Sending a bundle's id in
 * `product_id` would charge for whatever PRODUCT holds that id, or 422. CheckoutItem now
 * accepts exactly one of product_id / bundle_id, and this is the only place that decides which.
 *
 * Returns null for a line with no usable id, so the caller can drop it deliberately rather than
 * posting `{product_id: null}` and getting a validation error about a field the buyer never saw.
 */
export function toCheckoutLine(item) {
  const id = resolveProductId(item);
  if (!id) return null;
  const quantity = item?.quantity;
  return lineKind(item) === LINE_KIND_BUNDLE
    ? { bundle_id: id, quantity }
    : { product_id: id, quantity };
}

/**
 * Normalise a bundle into a CART LINE.
 *
 * ⛔ A BUNDLE IS NOT SHAPED LIKE A CART LINE, AND SPREADING IT RAW PRODUCES $NaN.
 *
 * The cart reduces over `{price, quantity}`. A bundle has no `price` field at all -- its money
 * field is `bundle_price` -- so a raw spread leaves `item.price` undefined, `undefined * qty` is
 * NaN, and cartTotal becomes NaN the moment a bundle is added. The cart and the checkout then
 * both display "$NaN". Reproduced before fixing.
 *
 * ⚠ AND IT ARRIVES AS A STRING. bundle_price is Decimal on PublicBundleListResponse, and
 * pydantic v2 serialises Decimal to a JSON string, so it is coerced here rather than relied on
 * to behave like a number by accident.
 *
 * Normalising HERE, once, is what stops the next surface that adds a bundle (a detail page, a
 * "buy again" button) rediscovering this the hard way.
 */
export function bundleToCartLine(bundle) {
  const price = Number(bundle?.bundle_price);
  return {
    ...bundle,
    kind: LINE_KIND_BUNDLE,
    // A bundle with no usable price is priced at 0 rather than NaN: a wrong number is visible
    // and arguable, NaN is a broken page. The server re-prices from bundle_price at checkout
    // regardless, so this value never decides what is charged.
    price: Number.isNaN(price) ? 0 : price,
  };
}
